/**
 * CommandPalette — global Cmd-K / Ctrl-K palette for the Social app (lane B6, R4 #19).
 *
 * Mounted ONCE via the shell's `headerActions` (App.tsx), next to the client
 * switcher and the notification bell. Opens on Cmd-K / Ctrl-K anywhere in the
 * app, or from the header search button.
 *
 * Sections:
 *   - Actions — New post, New design, Open inbox: needs reply, Switch client…
 *     ("Switch client…" swaps the list to the switcher's own brand list).
 *   - Navigate — derived from the sidebar config passed in (`groups`), never a
 *     duplicated list (`lib/commandPalette.navItemsFromSidebar`).
 *   - Posts / Designs / Content ideas — live search (200 ms debounce, top 5
 *     each, scoped to the selected client) over the existing list endpoints.
 *
 * Built on the existing `@plerity/ui-core` `Modal` (focus trap, Esc to close,
 * focus returns to the opener) — no new dependency. The search input is the
 * dialog's first focusable element, so Modal focuses it on open (no
 * `autoFocus`: that would focus it BEFORE Modal records the opener, and focus
 * would not return to the header button on close). The input is an ARIA
 * combobox driving a listbox via `aria-activedescendant`: ↑/↓/Home/End move,
 * Enter runs, Esc closes; Backspace on an empty client search goes back.
 *
 * @module components/command-palette/CommandPalette
 */

import type { SidebarGroup } from "@plerity/dashboard-shell";
import { Modal } from "@plerity/ui-core";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CornerDownLeft, FileText, Inbox, Lightbulb, Palette, PenSquare, Search, Users } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClientBrands } from "../../api/clientBrands";
import { usePaletteSearch } from "../../api/commandPalette";
import { useClientBrandStore } from "../../lib/client-brand";
import { brandOptions } from "../../lib/clientHealth";
import {
  excerpt,
  filterActions,
  filterNavItems,
  isPaletteShortcut,
  MIN_SEARCH_CHARS,
  navItemsFromSidebar,
  PALETTE_ACTIONS,
  type PaletteActionId,
  type PaletteIcon,
  RESULTS_PER_SECTION,
  SEARCH_DEBOUNCE_MS,
  shortcutLabel,
} from "../../lib/commandPalette";
import { toPlainText } from "../../lib/contentIdeas";

interface Row {
  key: string;
  section: string;
  label: string;
  hint?: string;
  icon?: PaletteIcon;
  run: () => void;
}

const ACTION_ICONS: Record<PaletteActionId, PaletteIcon> = {
  "new-post": PenSquare,
  "new-design": Palette,
  "inbox-needs-reply": Inbox,
  "switch-client": Users,
};

export function CommandPalette({ groups }: { groups: SidebarGroup[] }) {
  const [open, setOpen] = useState(false);
  const shortcut = useMemo(() => shortcutLabel(), []);

  useEffect(() => {
    // Capture phase on window + stopPropagation: the shell's assistant ChatWidget
    // (@plerity/chat-widget) ALSO toggles on Cmd/Ctrl+K via a document listener, so
    // without this one keypress opened both the palette and the chat panel.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!isPaletteShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div data-help-context="social-command-palette">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-keyshortcuts="Meta+K Control+K"
        aria-label={`Search and jump to (${shortcut})`}
        className="inline-flex h-9 items-center gap-2 rounded-md px-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100 md:border md:border-gray-300 md:dark:border-gray-600"
      >
        <Search className="h-5 w-5 md:h-4 md:w-4" aria-hidden="true" />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden rounded border border-gray-300 px-1 font-sans text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400 md:inline">
          {shortcut}
        </kbd>
      </button>
      {open && <PaletteDialog groups={groups} onClose={() => setOpen(false)} />}
    </div>
  );
}

function PaletteDialog({ groups, onClose }: { groups: SidebarGroup[]; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listId = useId();
  const [mode, setMode] = useState<"root" | "clients">("root");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);

  const brandId = useClientBrandStore((s) => s.selectedBrandId);
  const setSelectedBrandId = useClientBrandStore((s) => s.setSelectedBrandId);
  const brandsQuery = useClientBrands();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const term = debounced.trim();
  const searching = mode === "root" && term.length >= MIN_SEARCH_CHARS;
  const { posts, designs, ideas } = usePaletteSearch(term, brandId, searching);
  const navItems = useMemo(() => navItemsFromSidebar(groups), [groups]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: go/onClose only close the dialog + navigate (stable per open); rows rebuild on data + query changes.
  const rows: Row[] = useMemo(() => {
    if (mode === "clients") {
      const brands = brandsQuery.data?.items ?? [];
      const out: Row[] = [];
      const pick = (id: string | null) => () => {
        setSelectedBrandId(id);
        void queryClient.invalidateQueries();
        onClose();
      };
      if (!query.trim()) {
        out.push({ key: "client-all", section: "Clients", label: "All clients", icon: Users, run: pick(null) });
      }
      for (const o of brandOptions(brands, query)) {
        out.push({
          key: `client-${o.id}`,
          section: "Clients",
          label: o.depth === 1 && o.parentName ? `${o.name} (${o.parentName})` : o.name,
          hint: o.id === brandId ? "Current" : undefined,
          run: pick(o.id),
        });
      }
      return out;
    }

    const out: Row[] = [];
    for (const a of filterActions(PALETTE_ACTIONS, query)) {
      out.push({
        key: `action-${a.id}`,
        section: "Actions",
        label: a.label,
        icon: ACTION_ICONS[a.id],
        run:
          a.id === "switch-client"
            ? () => {
                setMode("clients");
                setQuery("");
                setActive(0);
              }
            : () => go(a.path ?? "/social/overview"),
      });
    }
    for (const n of filterNavItems(navItems, query)) {
      out.push({
        key: `nav-${n.path}`,
        section: "Navigate",
        label: n.label,
        hint: n.section || undefined,
        icon: n.icon,
        run: () => go(n.path),
      });
    }
    if (searching) {
      for (const p of (posts.data?.items ?? []).slice(0, RESULTS_PER_SECTION)) {
        out.push({
          key: `post-${p.id}`,
          section: "Posts",
          label: excerpt(p.content),
          hint: p.status,
          icon: FileText,
          run: () => go(`/social/posts/${p.id}`),
        });
      }
      for (const d of (designs.data?.items ?? []).slice(0, RESULTS_PER_SECTION)) {
        out.push({
          key: `design-${d.id}`,
          section: "Designs",
          label: d.name || "Untitled design",
          hint: d.preset ?? undefined,
          icon: Palette,
          run: () => go(`/social/designs/${d.id}`),
        });
      }
      for (const i of (ideas.data?.ideas ?? []).slice(0, RESULTS_PER_SECTION)) {
        out.push({
          key: `idea-${i.id}`,
          section: "Content ideas",
          label: toPlainText(i.title),
          hint: toPlainText(i.format),
          icon: Lightbulb,
          run: () => go(`/social/ideas?idea=${i.id}`),
        });
      }
    }
    return out;
  }, [
    mode,
    query,
    navItems,
    searching,
    posts.data,
    designs.data,
    ideas.data,
    brandsQuery.data,
    brandId,
    setSelectedBrandId,
    queryClient,
  ]);

  const safeActive = rows.length === 0 ? -1 : Math.min(active, rows.length - 1);

  useEffect(() => {
    if (safeActive < 0) return;
    document.getElementById(`${listId}-opt-${safeActive}`)?.scrollIntoView?.({ block: "nearest" });
  }, [safeActive, listId]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && mode === "clients" && query === "") {
      e.preventDefault();
      setMode("root");
      setActive(0);
      return;
    }
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((safeActive + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((safeActive - 1 + rows.length) % rows.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(rows.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      rows[safeActive]?.run();
    }
  }

  const live = searching
    ? [
        { name: "posts", label: "posts", q: posts },
        { name: "designs", label: "designs", q: designs },
        { name: "ideas", label: "content ideas", q: ideas },
      ]
    : [];
  const pending = query.trim() !== term || live.some((s) => s.q.isFetching);
  const failed = live.filter((s) => s.q.isError);

  return (
    <Modal isOpen onClose={onClose} ariaLabel="Search and jump to" showCloseButton={false} size="lg">
      <div className="-mx-6 -my-4">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          {mode === "clients" ? (
            <button
              type="button"
              onClick={() => {
                setMode("root");
                setQuery("");
                setActive(0);
              }}
              aria-label="Back to all commands"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-400 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            <Search className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          )}
          <input
            type="text"
            role="combobox"
            aria-expanded={true}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={safeActive >= 0 ? `${listId}-opt-${safeActive}` : undefined}
            aria-label={mode === "clients" ? "Search clients" : "Search pages, actions, posts, designs and ideas"}
            placeholder={mode === "clients" ? "Switch to client…" : "Search or jump to…"}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="w-full rounded bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>

        <div
          id={listId}
          role="listbox"
          aria-label={mode === "clients" ? "Clients" : "Commands and results"}
          className="max-h-96 overflow-y-auto py-2"
        >
          {rows.map((row, idx) => {
            const showHeader = rows[idx - 1]?.section !== row.section;
            const Icon = row.icon;
            const isActive = idx === safeActive;
            return (
              <div key={row.key} role="presentation">
                {showHeader && (
                  <p
                    role="presentation"
                    className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {row.section}
                  </p>
                )}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled by the combobox input (aria-activedescendant pattern). */}
                <div
                  id={`${listId}-opt-${idx}`}
                  role="option"
                  aria-selected={isActive}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => row.run()}
                  className={`mx-2 flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm ${
                    isActive
                      ? "bg-info-50 text-info-900 dark:bg-gray-700 dark:text-gray-50"
                      : "text-gray-800 dark:text-gray-100"
                  }`}
                >
                  {Icon ? (
                    <Icon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-400" aria-hidden="true" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {row.hint && (
                    <span className="shrink-0 truncate text-xs text-gray-500 dark:text-gray-400">{row.hint}</span>
                  )}
                  {isActive && (
                    <CornerDownLeft
                      className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-1 border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {mode === "clients" && brandsQuery.isError && (
            <p role="alert" className="text-red-600 dark:text-red-400">
              Couldn't load clients.{" "}
              <button
                type="button"
                onClick={() => brandsQuery.refetch()}
                className="font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
              >
                Retry
              </button>
            </p>
          )}
          {failed.length > 0 && (
            <p role="alert" className="text-red-600 dark:text-red-400">
              Couldn't search {failed.map((s) => s.label).join(" or ")}.{" "}
              <button
                type="button"
                onClick={() => {
                  for (const s of failed) void s.q.refetch();
                }}
                className="font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
              >
                Retry
              </button>
            </p>
          )}
          {/* biome-ignore lint/a11y/useSemanticElements: role=status is the WAI-ARIA live-region pattern for this hint line; <output> is for form results. */}
          <p role="status" aria-live="polite">
            {pending && searching
              ? "Searching…"
              : rows.length === 0
                ? mode === "clients"
                  ? brandsQuery.isLoading
                    ? "Loading clients…"
                    : "No clients match."
                  : query.trim().length > 0 && query.trim().length < MIN_SEARCH_CHARS
                    ? "Keep typing to search posts, designs and ideas."
                    : "No matches."
                : `${rows.length} ${rows.length === 1 ? "result" : "results"} · ↑↓ to move · Enter to open · Esc to close`}
          </p>
        </div>
      </div>
    </Modal>
  );
}
